import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const setup2FA = createAsyncThunk('twoFactor/setup', async (_, { rejectWithValue }) => {
    try {
        const response = await api.post('/2fa/setup');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to setup 2FA');
    }
});

export const verify2FASetup = createAsyncThunk('twoFactor/verifySetup', async (token, { rejectWithValue }) => {
    try {
        const response = await api.post('/2fa/verify-setup', { token });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to verify 2FA setup');
    }
});

export const verify2FA = createAsyncThunk('twoFactor/verify', async (data, { rejectWithValue }) => {
    try {
        const response = await api.post('/2fa/verify', data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to verify 2FA');
    }
});

export const disable2FA = createAsyncThunk('twoFactor/disable', async (data, { rejectWithValue }) => {
    try {
        const response = await api.post('/2fa/disable', data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to disable 2FA');
    }
});

export const get2FAStatus = createAsyncThunk('twoFactor/status', async (_, { rejectWithValue }) => {
    try {
        const response = await api.get('/2fa/status');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to get 2FA status');
    }
});

export const getTrustedDevices = createAsyncThunk('twoFactor/trustedDevices', async (_, { rejectWithValue }) => {
    try {
        const response = await api.get('/2fa/trusted-devices');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to get trusted devices');
    }
});

export const removeTrustedDevice = createAsyncThunk('twoFactor/removeTrustedDevice', async (deviceId, { rejectWithValue }) => {
    try {
        await api.delete(`/2fa/trusted-devices/${deviceId}`);
        return deviceId;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to remove trusted device');
    }
});

const initialState = {
    isEnabled: false,
    isLoading: false,
    setupData: null,
    backupCodesCount: 0,
    trustedDevicesCount: 0,
    lastVerified: null,
    trustedDevices: [],
    error: null,
    requiresVerification: false,
    verificationFor: null,
};

const twoFactorSlice = createSlice({
    name: 'twoFactor',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSetupData: (state) => {
            state.setupData = null;
        },
        requireVerification: (state, action) => {
            state.requiresVerification = true;
            state.verificationFor = action.payload;
        },
        clearVerificationRequirement: (state) => {
            state.requiresVerification = false;
            state.verificationFor = null;
        },
        setVerificationHeaders: (state, action) => {
            state.verificationTimestamp = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setup2FA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(setup2FA.fulfilled, (state, action) => {
                state.isLoading = false;
                state.setupData = action.payload;
            })
            .addCase(setup2FA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(verify2FASetup.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(verify2FASetup.fulfilled, (state) => {
                state.isLoading = false;
                state.isEnabled = true;
                state.setupData = null;
            })
            .addCase(verify2FASetup.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(verify2FA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(verify2FA.fulfilled, (state, action) => {
                state.isLoading = false;
                state.requiresVerification = false;
                state.verificationFor = null;
                state.backupCodesCount = action.payload.backupCodesRemaining;
            })
            .addCase(verify2FA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(disable2FA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(disable2FA.fulfilled, (state) => {
                state.isLoading = false;
                state.isEnabled = false;
                state.backupCodesCount = 0;
                state.trustedDevicesCount = 0;
                state.trustedDevices = [];
            })
            .addCase(disable2FA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(get2FAStatus.fulfilled, (state, action) => {
                state.isEnabled = action.payload.isEnabled;
                state.backupCodesCount = action.payload.backupCodesCount;
                state.trustedDevicesCount = action.payload.trustedDevicesCount;
                state.lastVerified = action.payload.lastVerified;
            })
            .addCase(getTrustedDevices.fulfilled, (state, action) => {
                state.trustedDevices = action.payload.devices;
            })
            .addCase(removeTrustedDevice.fulfilled, (state, action) => {
                state.trustedDevices = state.trustedDevices.filter(
                    device => device.deviceId !== action.payload
                );
                state.trustedDevicesCount = state.trustedDevices.length;
            });
    },
});

export const { 
    clearError, 
    clearSetupData, 
    requireVerification, 
    clearVerificationRequirement,
    setVerificationHeaders 
} = twoFactorSlice.actions;

export default twoFactorSlice.reducer;